import { getApiBaseUrl } from "../config/runtimeConfig";

const apiBaseUrl = getApiBaseUrl();

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export type CashflowEventType = "Inflow" | "Outflow";

/** Origem de um movimento previsto. Inflow ⇒ milestone|invoice|rent; outflow ⇒ loan|expense|tax. */
export type CashflowCategory = "milestone" | "invoice" | "rent" | "loan" | "expense" | "tax";

export interface CashflowEvent {
  date: string;
  type: CashflowEventType;
  category: CashflowCategory;
  amount: number;
  description: string;
  reference: string;
}

export interface CashflowCalendar {
  days: number;
  rangeStart: string;
  rangeEnd: string;
  totalInflows: number;
  totalOutflows: number;
  net: number;
  events: CashflowEvent[];
}

/** Indicador semáforo do saldo projetado no fim do mês. */
export type TreasuryLevel = "green" | "yellow" | "red";

/** Linha de entrada/saída prevista até ao fim do mês. */
export interface ForecastItem {
  category: CashflowCategory;
  date: string;
  amount: number;
  description: string;
  reference: string;
}

export type TreasuryAlertSeverity = "danger" | "warning" | "info";

export interface TreasuryAlert {
  severity: TreasuryAlertSeverity;
  type: string;
  message: string;
  date: string | null;
  amount: number | null;
}

export interface TreasuryForecast {
  asOf: string;
  monthEnd: string;
  openingBalance: number;
  openingBalanceAsOf: string | null;
  inflowsTotal: number;
  outflowsTotal: number;
  projectedBalance: number;
  level: TreasuryLevel;
  inflows: ForecastItem[];
  outflows: ForecastItem[];
  alerts: TreasuryAlert[];
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }

  return (await response.json()) as T;
}

export function getCashflowCalendar(days?: number | null): Promise<CashflowCalendar> {
  const query = days !== null && days !== undefined ? `?days=${days}` : "";
  return request<CashflowCalendar>(`/api/treasury/cashflow-calendar${query}`);
}

export function getTreasuryForecast(asOf?: string | null): Promise<TreasuryForecast> {
  const query = asOf ? `?asOf=${encodeURIComponent(asOf)}` : "";
  return request<TreasuryForecast>(`/api/treasury/forecast${query}`);
}
